import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Fade,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { T } from "./adminTheme";
import LockResetIcon from "@mui/icons-material/LockReset";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SettingsBackupRestoreIcon from "@mui/icons-material/SettingsBackupRestore";
import SearchIcon from "@mui/icons-material/Search";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";

import {
  SectionCard,
  StyledTableRow,
  TabHeader,
  BoolChip,
  AvailabilityChip,
  InlinePromptDialog,
  USER_ROLE_OPTIONS,
  FilterableSortableTableHead,
  useSortableTable,
  applyColumnFilters,
  RowActionButton,
} from "./shared";

import {
  checkUserGlobalStatus,
  releaseUserLockSafely,
} from "./recoveryHelpers";

// ✅ Optimized dialog - prevents typing delay
import UserCreateDialog from "./UserCreateDialog";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const COLUMNS = [
  { id: "Employee_ID",        label: "Employee ID",      minWidth: 110 },
  { id: "User_Category",      label: "Role",             minWidth: 90  },
  { id: "ActiveStatus",       label: "Status",           minWidth: 110 },
  { id: "avaliability",       label: "Session",          minWidth: 130 },
  { id: "hasEmployeeProfile", label: "Employee Profile", minWidth: 140 },
  { id: "_actions",           label: "Actions", sortable: false, align: "right", minWidth: 90 },
];

function getUserRecommendationMessage(status) {
  if (!status) return "Unable to analyze user recovery status.";

  if (status.recommendation === "USE_MAN_HOUR_RECOVERY") {
    return "This user/supervisor has active man-hour records or selected man-hour workstations. Please use Man-hour Recovery.";
  }

  if (status.recommendation === "USE_MACHINE_HOUR_RECOVERY") {
    return "This user has active machine-hour records. Please use Session Recovery.";
  }

  if (status.recommendation === "CHECK_BOTH_MACHINE_HOUR_AND_MAN_HOUR_RECOVERY") {
    return "This user has active records in both machine-hour and man-hour sections. Please check both recovery sections.";
  }

  if (status.recommendation === "DIRECT_RELEASE_ALLOWED") {
    return "No active machine-hour or man-hour records found. Direct user lock release is allowed.";
  }

  return "No active machine-hour or man-hour records found.";
}

export default function UsersTab({
  onSnack,
  goToEmployees,
  goToSessionRecovery,
  goToManhourRecovery,
  adminUsername,
  externalSearch,
  setExternalSearch,
}) {
  const theme = useTheme();
  const t = T(theme.palette.mode);

  // ✅ Removed form state - now in UserCreateDialog (prevents typing delay)
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});
  const search = externalSearch ?? "";
  const setSearch = setExternalSearch ?? (() => {});
  const [openCreate, setOpenCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  // ✅ Removed form state - now in UserCreateDialog
  const [roleDlg, setRoleDlg] = useState({
    open: false,
    employeeId: null,
    value: "operator",
  });
  const [pwdDlg, setPwdDlg] = useState({
    open: false,
    employeeId: null,
    value: "",
  });

  const [releaseDlg, setReleaseDlg] = useState({
    open: false,
    userId: null,
    loading: false,
    status: null,
    actionLoading: false,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE_URL}/admin/users-with-employee-link`);
      setList(r.data || []);
      setColumnFilters({});
    } catch {
      onSnack("Failed to load user accounts.", "error");
    } finally {
      setLoading(false);
    }
  }, [onSnack]);

  useEffect(() => {
    load();
  }, [load]);

  const searchFiltered = useMemo(() => {
    const q = search.toLowerCase();

    return list.filter((u) => (
      String(u.Employee_ID).toLowerCase().includes(q) ||
      u.User_Category?.toLowerCase().includes(q)
    ));
  }, [list, search]);

  const columnFilteredRows = useMemo(
    () => applyColumnFilters(searchFiltered, columnFilters),
    [searchFiltered, columnFilters]
  );

  const { sortedRows: rows, sortField, sortDir, handleSort } =
    useSortableTable(columnFilteredRows, "Employee_ID");

  const save = async (formData) => {  // ✅ Now accepts form data from dialog
    if (!formData.Employee_ID || !formData.Password || !formData.User_Category) {
      onSnack("Please fill all required user fields.", "warning");
      return;
    }

    setSaving(true);
    try {
      await axios.post(`${API_BASE_URL}/admin/createUser`, formData);  // ✅ Use formData
      onSnack("User account created successfully.");
      setOpenCreate(false);
      load();
    } catch (err) {
      onSnack(
        err?.response?.data?.message || "Failed to create user account.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const updateUserStatus = async (employeeId, ActiveStatus) => {
    try {
      await axios.put(`${API_BASE_URL}/admin/users/${employeeId}/status`, {
        ActiveStatus,
      });
      onSnack("User status updated.");
      load();
    } catch (err) {
      onSnack(
        err?.response?.data?.message || "Failed to update user status.",
        "error"
      );
    }
  };

  const openReleaseDialog = async (userId) => {
    setReleaseDlg({
      open: true,
      userId,
      loading: true,
      status: null,
      actionLoading: false,
    });

    try {
      const status = await checkUserGlobalStatus(userId);

      setReleaseDlg({
        open: true,
        userId,
        loading: false,
        status,
        actionLoading: false,
      });
    } catch (err) {
      setReleaseDlg({
        open: false,
        userId: null,
        loading: false,
        status: null,
        actionLoading: false,
      });

      onSnack(
        err?.response?.data?.error || "Failed to analyze user global status.",
        "error"
      );
    }
  };

  const closeReleaseDialog = () => {
    if (releaseDlg.actionLoading) return;

    setReleaseDlg({
      open: false,
      userId: null,
      loading: false,
      status: null,
      actionLoading: false,
    });
  };

  const confirmSafeUserRelease = async () => {
    const { userId, status } = releaseDlg;

    if (!status?.canDirectRelease) {
      onSnack("Direct release is blocked. Use the recommended recovery section.", "warning");
      return;
    }

    setReleaseDlg((s) => ({ ...s, actionLoading: true }));

    try {
      await releaseUserLockSafely({
        userId,
        cleanedBy: adminUsername || "admin",
        reason: "Manual user release from Admin Users tab after global recovery check",
      });

      onSnack("User lock released successfully.", "success");
      closeReleaseDialog();
      load();
    } catch (err) {
      onSnack(err?.response?.data?.error || "User release failed.", "error");
      setReleaseDlg((s) => ({ ...s, actionLoading: false }));
    }
  };

  const goToMachineRecovery = () => {
    const userId = releaseDlg.userId;
    closeReleaseDialog();

    if (typeof goToSessionRecovery === "function") {
      goToSessionRecovery({ employeeId: userId });
      return;
    }

    onSnack(`Open Session Recovery and search Employee ID ${userId}.`, "info");
  };

  const goToManhourRecoverySection = () => {
    const userId = releaseDlg.userId;
    const status = releaseDlg.status;
    closeReleaseDialog();

    const firstOpenShift = status?.manHourSupervisor?.openShifts?.[0];
    const firstMachine = status?.manHourSupervisor?.activeMachines?.[0];

    if (typeof goToManhourRecovery === "function") {
      goToManhourRecovery({
        supervisorId: userId,
        stationCode: firstOpenShift?.station_code || firstMachine?.MachineCode,
      });
      return;
    }

    onSnack(`Open Man-hour Recovery and search Supervisor ID ${userId}.`, "info");
  };

  const handleRoleConfirm = async () => {
    const { employeeId, value } = roleDlg;
    setRoleDlg({ open: false, employeeId: null, value: "operator" });

    try {
      await axios.put(`${API_BASE_URL}/admin/users/${employeeId}/role`, {
        User_Category: value,
      });
      onSnack("User role updated.");
      load();
    } catch (err) {
      onSnack(err?.response?.data?.message || "Failed to update user role.", "error");
    }
  };

  const handlePasswordConfirm = async () => {
    const { employeeId, value } = pwdDlg;
    setPwdDlg({ open: false, employeeId: null, value: "" });

    try {
      await axios.put(`${API_BASE_URL}/admin/users/${employeeId}/password`, {
        Password: value,
      });
      onSnack("User password updated.");
      load();
    } catch (err) {
      onSnack(err?.response?.data?.message || "Failed to update password.", "error");
    }
  };

  const handleViewEmployee = (employeeId) => {
    if (typeof goToEmployees === "function") {
      goToEmployees(employeeId);
      return;
    }
    onSnack(`Go to Employees tab and search Employee ID ${employeeId}.`, "info");
  };

  return (
    <Fade in>
      <Box>
        <SectionCard>
          <TabHeader
            count={rows.length}
            noun="User Accounts"
            search={search}
            onSearch={setSearch}
            onRefresh={load}
            onAdd={() => {
              setOpenCreate(true);  // ✅ Dialog handles its own form reset
            }}
            btnLabel="Create User Account"
            btnColor="linear-gradient(90deg,#8b5cf6,#7c3aed)"
          />
          <Divider />

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress size={30} />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <FilterableSortableTableHead
                  columns={COLUMNS}
                  sourceRows={searchFiltered}
                  sortField={sortField}
                  sortDir={sortDir}
                  onSort={handleSort}
                  columnFilters={columnFilters}
                  onColumnFiltersChange={setColumnFilters}
                />
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={COLUMNS.length}
                        sx={{ textAlign: "center", py: 5, color: t.muted }}
                      >
                        {search
                          ? "No user accounts match your search."
                          : "No user accounts found."}
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((u) => {
                      const hasEmployeeProfile = Number(u.hasEmployeeProfile) === 1;
                      const isActive = Number(u.ActiveStatus) === 1;

                      return (
                        <StyledTableRow key={u.Employee_ID}>
                          <TableCell
                            sx={{ fontWeight: 700, fontFamily: "monospace", fontSize: 12 }}
                          >
                            {u.Employee_ID}
                          </TableCell>

                          <TableCell>{u.User_Category || "—"}</TableCell>

                          {/* Status — inline toggle */}
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                              <BoolChip value={u.ActiveStatus} />
                              <Box sx={{
                                opacity: 0,
                                ".MuiTableRow-root:hover &": { opacity: 1 },
                                transition: "opacity 0.15s ease",
                              }}>
                                <RowActionButton
                                  title={isActive ? "Deactivate user" : "Activate user"}
                                  icon={<PowerSettingsNewIcon />}
                                  color={isActive ? "error" : "success"}
                                  onClick={() => updateUserStatus(u.Employee_ID, isActive ? 0 : 1)}
                                />
                              </Box>
                            </Box>
                          </TableCell>

                          {/* Session — inline release */}
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                              <AvailabilityChip value={u.avaliability} />
                              <Box sx={{
                                opacity: 0,
                                ".MuiTableRow-root:hover &": { opacity: 1 },
                                transition: "opacity 0.15s ease",
                              }}>
                                <RowActionButton
                                  title="Release user session — global machine-hour / man-hour check"
                                  icon={<SettingsBackupRestoreIcon />}
                                  color="warning"
                                  onClick={() => openReleaseDialog(u.Employee_ID)}
                                />
                              </Box>
                            </Box>
                          </TableCell>

                          {/* Employee Profile — inline go-to */}
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                              <Chip
                                size="small"
                                label={hasEmployeeProfile ? "Linked" : "Missing"}
                                color={hasEmployeeProfile ? "success" : "warning"}
                              />
                              <Box sx={{
                                opacity: 0,
                                ".MuiTableRow-root:hover &": { opacity: 1 },
                                transition: "opacity 0.15s ease",
                              }}>
                                <RowActionButton
                                  title="View in Employees tab"
                                  icon={<SearchIcon />}
                                  color="primary"
                                  onClick={() => handleViewEmployee(u.Employee_ID)}
                                />
                              </Box>
                            </Box>
                          </TableCell>

                          {/* Actions — role + password only */}
                          <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                            <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end", alignItems: "center" }}>
                              <RowActionButton
                                title="Change user role"
                                icon={<AdminPanelSettingsIcon />}
                                color="primary"
                                onClick={() => setRoleDlg({ open: true, employeeId: u.Employee_ID, value: u.User_Category || "operator" })}
                              />
                              <RowActionButton
                                title="Reset password"
                                icon={<LockResetIcon />}
                                color="error"
                                onClick={() => setPwdDlg({ open: true, employeeId: u.Employee_ID, value: "" })}
                              />
                            </Box>
                          </TableCell>
                        </StyledTableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </SectionCard>

        {/* ✅ Optimized create dialog - no typing delay */}
        <UserCreateDialog
          open={openCreate}
          onClose={() => setOpenCreate(false)}
          onSave={save}
          saving={saving}
        />

        <InlinePromptDialog
          open={roleDlg.open}
          title="Change User Role"
          label="New role"
          value={roleDlg.value}
          onChange={(v) => setRoleDlg((s) => ({ ...s, value: v }))}
          onConfirm={handleRoleConfirm}
          onClose={() =>
            setRoleDlg({ open: false, employeeId: null, value: "operator" })
          }
          confirmLabel="Update Role"
          selectOptions={USER_ROLE_OPTIONS}
        />

        <InlinePromptDialog
          open={pwdDlg.open}
          title="Reset User Password"
          label="New password"
          value={pwdDlg.value}
          onChange={(v) => setPwdDlg((s) => ({ ...s, value: v }))}
          onConfirm={handlePasswordConfirm}
          onClose={() => setPwdDlg({ open: false, employeeId: null, value: "" })}
          confirmLabel="Reset Password"
        />

        <Dialog
          open={releaseDlg.open}
          onClose={closeReleaseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Stack direction="row" spacing={1} alignItems="center">
              {releaseDlg.status?.canDirectRelease ? (
                <CheckCircleIcon color="success" />
              ) : (
                <WarningAmberIcon color="warning" />
              )}
              <Typography variant="h6" fontWeight={700}>
                User Global Recovery Check
              </Typography>
            </Stack>
          </DialogTitle>

          <DialogContent>
            {releaseDlg.loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Stack spacing={2}>
                <Alert severity={releaseDlg.status?.canDirectRelease ? "success" : "warning"}>
                  {getUserRecommendationMessage(releaseDlg.status)}
                </Alert>

                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    User / Employee ID
                  </Typography>
                  <Typography variant="body2">{releaseDlg.userId}</Typography>
                </Box>

                {releaseDlg.status && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          border: `1px solid ${t.border}`,
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={700}>
                          Machine-hour records
                        </Typography>
                        <Typography variant="body2">
                          Open shifts:{" "}
                          {releaseDlg.status.machineHour?.openShiftCount || 0}
                        </Typography>
                        <Typography variant="body2">
                          Open operations:{" "}
                          {releaseDlg.status.machineHour?.openOperationCount || 0}
                        </Typography>
                        <Typography variant="body2">
                          Open jobs:{" "}
                          {releaseDlg.status.machineHour?.openJobCount || 0}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          border: `1px solid ${t.border}`,
                        }}
                      >
                        <Typography variant="subtitle2" fontWeight={700}>
                          Man-hour supervisor records
                        </Typography>
                        <Typography variant="body2">
                          Active machines:{" "}
                          {releaseDlg.status.manHourSupervisor?.activeMachineCount ||
                            0}
                        </Typography>
                        <Typography variant="body2">
                          Open shifts:{" "}
                          {releaseDlg.status.manHourSupervisor?.openShiftCount || 0}
                        </Typography>
                        <Typography variant="body2">
                          Open details:{" "}
                          {releaseDlg.status.manHourSupervisor?.openDetailCount || 0}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                )}

                {releaseDlg.status?.canDirectRelease && (
                  <Alert severity="info">
                    Confirming will release this user lock from the common user
                    table by setting user availability to available.
                  </Alert>
                )}
              </Stack>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 2 }}>
            <Button onClick={closeReleaseDialog} disabled={releaseDlg.actionLoading}>
              Cancel
            </Button>

            {!releaseDlg.loading && !releaseDlg.status?.canDirectRelease && (
              <>
                {(releaseDlg.status?.recommendation === "USE_MACHINE_HOUR_RECOVERY" ||
                  releaseDlg.status?.recommendation ===
                    "CHECK_BOTH_MACHINE_HOUR_AND_MAN_HOUR_RECOVERY") && (
                  <Button
                    variant="outlined"
                    startIcon={<CleaningServicesIcon />}
                    onClick={goToMachineRecovery}
                  >
                    Open Session Recovery
                  </Button>
                )}

                {(releaseDlg.status?.recommendation === "USE_MAN_HOUR_RECOVERY" ||
                  releaseDlg.status?.recommendation ===
                    "CHECK_BOTH_MACHINE_HOUR_AND_MAN_HOUR_RECOVERY") && (
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<CleaningServicesIcon />}
                    onClick={goToManhourRecoverySection}
                  >
                    Open Man-hour Recovery
                  </Button>
                )}
              </>
            )}

            {!releaseDlg.loading && releaseDlg.status?.canDirectRelease && (
              <Button
                variant="contained"
                color="success"
                onClick={confirmSafeUserRelease}
                disabled={releaseDlg.actionLoading}
                startIcon={
                  releaseDlg.actionLoading ? (
                    <CircularProgress size={15} color="inherit" />
                  ) : (
                    <CheckCircleIcon />
                  )
                }
              >
                Release User Lock
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </Fade>
  );
}